import styled from '@emotion/styled'
import React, { Fragment, type FunctionComponent } from 'react'
import { InformationIcon } from '@instacart/ids-core'
import {
  fontSize,
  Tooltip,
  EINA_01_FONT,
  fontWeight,
  spacing,
  colors,
} from '@retailer-platform/shared-components'
import qs from 'query-string'
import moment from 'moment'
import groupBy from 'lodash/groupBy'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { StaffingAlertLiveBadge } from 'domains/shifts/components/components/staffing-alert/StaffingAlertLiveBadge'
import { type ShiftsRouteQuery } from 'domains/shifts/components/types'
import CalendarIcon from 'domains/shifts/assets/calendar.svg'
import { type RecommendationTypeEnum } from 'domains/shifts/api'
import { useRecommendedShiftsContext } from 'domains/shifts/components/contexts/RecommendationShiftsContext'
import { StaffingAlertCard } from './StaffingAlertCard'

const IconWrapper = styled.div({
  marginLeft: spacing.X8,
})

const StaffingAlertContainer = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  padding: `${spacing.X24} ${spacing.X16}`,
  borderTop: `1px solid ${colors.GRAYSCALE.X20}`,
  justifyContent: 'flex-start',
})

const StaffingAlertTitleColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '260px',
})

const StaffingAlertTitleText = styled.div({
  fontSize: fontSize.X23,
  lineHeight: '28px',
  fontFamily: EINA_01_FONT,
  fontWeight: fontWeight.SEMIBOLD,
  flexDirection: 'row',
  display: 'flex',
})

const StaffingAlertEmptyState = styled.div({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
})
const StaffingAlertEmptyStateImage = styled.img({
  width: spacing.X48,
  height: spacing.X48,
})

const StaffingAlertEmptyStateText = styled.div({
  color: colors.GRAYSCALE.X30,
  margin: `0 ${spacing.X4}`,
})

interface Props {
  zoneOffset: number
}

export const StaffingAlertRow: FunctionComponent<Props> = ({ zoneOffset }) => {
  const query = qs.parse(location.search) as ShiftsRouteQuery
  const queryDate = query.date
  // For Staffing alert, we only want to display alerts within 48 hours
  const isWithinTwoDays = moment(queryDate).isBefore(moment().startOf('day').add(2, 'days'))

  const messages = useDomainMessages({
    rowTitle: 'shiftsDomain.staffingAlert.rowTitle',
    rowSubTitle: 'shiftsDomain.staffingAlert.rowSubTitle',
    tooltip: 'shiftsDomain.staffingAlert.rowTitleToolTip',
    twoDaysInAdvance: 'shiftsDomain.staffingAlert.onlyAvailableTwoDays',
  })

  const { recommendations, allowedRecommendationTypes } = useRecommendedShiftsContext()
  const aggregatedRecommendations = groupBy(recommendations, 'recommendationType')

  allowedRecommendationTypes.forEach(type => {
    // Here we loop through all possible allowedRecommendationTypes from API response
    // If the type is not in aggregatedRecommendations (aka aggregatedRecommendations[type] === undefined)
    // We will manually add an empty array to this type
    // The end goal is to make sure the aggregatedRecommendations has all the possible types
    if (aggregatedRecommendations[type] === undefined) {
      aggregatedRecommendations[type] = []
    }
  })

  // NOTE: We want "Demand Surge" to always show before "No Runner"
  // At this moment, we only have these two types, a simple .sort() can provide a reliable solution
  // If in the future we have more types and we can't reply on ".sort()",
  // we will need to assign custom sort order to each Recommendation Key
  const recommendationsKeys = Object.keys(aggregatedRecommendations).sort()
  const renderAlertContent = () =>
    isWithinTwoDays ? (
      <Fragment>
        {recommendationsKeys.map(key => (
          <StaffingAlertCard
            key={key}
            alertCount={aggregatedRecommendations[key].length}
            recommendationType={key as RecommendationTypeEnum}
            recommendations={aggregatedRecommendations[key]}
            zoneOffset={zoneOffset}
          />
        ))}
      </Fragment>
    ) : (
      <StaffingAlertEmptyState>
        <StaffingAlertEmptyStateImage src={CalendarIcon} />
        <StaffingAlertEmptyStateText>{messages.twoDaysInAdvance}</StaffingAlertEmptyStateText>
      </StaffingAlertEmptyState>
    )

  return (
    <StaffingAlertContainer data-testid="staffingAlertContainer">
      <StaffingAlertTitleColumn>
        {isWithinTwoDays && <StaffingAlertLiveBadge data-testid="staffingAlertLiveBadge" />}
        <StaffingAlertTitleText>
          {messages.rowTitle}
          <Tooltip
            target={
              <IconWrapper>
                <InformationIcon size={16} color={'systemGrayscale30'} />
              </IconWrapper>
            }
            placement="bottom"
          >
            {messages.tooltip}
          </Tooltip>
        </StaffingAlertTitleText>
        <StaffingAlertTitleText style={{ fontSize: fontSize.X12, fontWeight: fontWeight.REGULAR }}>
          {messages.rowSubTitle}
        </StaffingAlertTitleText>
      </StaffingAlertTitleColumn>
      {renderAlertContent()}
    </StaffingAlertContainer>
  )
}
